import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';

const ActiveOrgContext = createContext();

export const useActiveOrg = () => {
    const context = useContext(ActiveOrgContext);
    if (!context) {
        throw new Error('useActiveOrg must be used within ActiveOrgProvider');
    }
    return context;
};

export const ActiveOrgProvider = ({ children }) => {
    const [activeOrg, setActiveOrgState] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetched, setFetched] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            // Profil ve organizasyonları paralel olarak çek (Önbellek çakışmasını önlemek için timestamp eklendi)
            const [meRes, orgsRes] = await Promise.all([
                API.get(`/me/?_t=${Date.now()}`),
                API.get(`/organizations/?_t=${Date.now()}`)
            ]);

            const user = meRes.data;
            const orgs = orgsRes.data;
            setOrganizations(orgs);

            if (user.is_superuser) {
                // Superadmin ise otomatik seçim yapma, doğrudan organizasyon seçimine yönlendir
                setActiveOrgState(null);
                localStorage.removeItem('activeOrgId');
            } else if (orgs.length === 1) {
                // Tek organizasyon varsa otomatik seç
                setActiveOrgState(orgs[0]);
                localStorage.setItem('activeOrgId', orgs[0].id);
            } else {
                // Birden fazla organizasyon varsa otomatik seçim yapma, seçime yönlendir
                setActiveOrgState(null);
                localStorage.removeItem('activeOrgId');
            }

            setFetched(true);
            setLoading(false);
        } catch (err) {
            console.error('Organizations fetch error:', err);
            setLoading(false);
            
            // Yetkisiz erişim (401/403) durumunda temizlik ve login'e yönlendirme (Sonsuz döngüyü önler)
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                clearActiveOrg();
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                navigate('/login');
            }
        }
    };

    // Rota değişikliklerini izleyerek token varsa ve henüz çekilmemişse verileri yükle
    useEffect(() => {
        const token = localStorage.getItem('token');
        const isAuthRoute = location.pathname === '/login' || location.pathname.startsWith('/auth/magic-link');
        
        if (token && !fetched && !isAuthRoute) {
            fetchOrganizations();
        }
    }, [location.pathname, fetched]);

    const setActiveOrg = (org) => {
        setActiveOrgState(org);
        if (org) {
            localStorage.setItem('activeOrgId', org.id);
        } else {
            localStorage.removeItem('activeOrgId');
        }
    };

    const clearActiveOrg = () => {
        setActiveOrgState(null);
        setOrganizations([]);
        setFetched(false);
        localStorage.removeItem('activeOrgId');
    };

    const hasMultipleOrgs = organizations.length > 1;
    const needsOrgSelection = hasMultipleOrgs && !activeOrg;

    const value = {
        activeOrg,
        setActiveOrg,
        clearActiveOrg,
        organizations,
        hasMultipleOrgs,
        needsOrgSelection,
        loading,
        refreshOrganizations: fetchOrganizations
    };

    return (
        <ActiveOrgContext.Provider value={value}>
            {children}
        </ActiveOrgContext.Provider>
    );
};

export default ActiveOrgContext;
